import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import {
  FormHelperText,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  ListSubheader,
  TextField,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CommonDropdown = ({
  fieldName,
  value,
  onChange,
  options,
  elementStyle,
  required,
  multiple,
  useFormik,
  displayMode, // "chips" | "comma"
  showSelectAll,
  enableSearch,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const showSearchBar = enableSearch && options.length > 10;

  const filterOptions = useMemo(() => {
    if (!showSearchBar || !searchTerm) return options;
    return options.filter((opt) => {
      const label = typeof opt === 'string' ? opt : opt.label;
      return label.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [options, searchTerm, showSearchBar]);

  const renderSelect = (fieldProps) => {
    const fieldValue = useFormik ? fieldProps.field.value || [] : value || [];

    const handleChange = (event) => {
      const newValue = event.target.value;

      // Handle Select All logic
      if (multiple && showSelectAll && newValue.includes('ALL')) {
        const allValues = options.map((opt) =>
          typeof opt === 'string' ? opt : opt.key
        );
        fieldProps.form?.setFieldValue?.(fieldName, allValues);
        onChange?.({ target: { value: allValues } });
        return;
      }

      if (useFormik) fieldProps.form.setFieldValue(fieldName, newValue);
      onChange?.(event);
    };

    const getOptionValue = (opt) =>
      typeof opt === 'string' ? opt : opt.key ?? opt.value;

    const getOptionLabel = (opt) =>
      typeof opt === 'string' ? opt : opt.label ?? opt.value;

    const renderValue = (selected) => {
      if (!multiple)
        return getOptionLabel(
          options.find((o) => getOptionValue(o) === selected) || selected
        );

      if (displayMode === 'chips') {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((val) => {
              const label = getOptionLabel(
                options.find((o) => getOptionValue(o) === val) || val
              );
              return <Chip key={val} label={label} size="small" sx={{ textTransform: 'none' }} />;
            })}
          </Box>
        );
      }

      return selected
        .map((val) =>
          getOptionLabel(options.find((o) => getOptionValue(o) === val) || val)
        )
        .join(', ');
    };

    const renderOptions = (opts) => {
      const menuItems = [];

      // Search bar (only if more than 10 options)
      if (showSearchBar) {
        menuItems.push(
          <ListSubheader
            key="search"
            sx={{
              p: 1,
              position: 'sticky',
              top: 0,
              bgcolor: 'white',
              zIndex: 1,
            }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()} // prevent closing
            />
          </ListSubheader>
        );
      }

      // Select All
      if (multiple && showSelectAll) {
        menuItems.push(
          <MenuItem key="ALL" value="ALL">
            <Checkbox
              checked={
                fieldValue.length === options.length && options.length > 0
              }
            />
            <ListItemText primary="Select All" />
          </MenuItem>
        );
      }

      // No matches
      if (opts.length === 0) {
        menuItems.push(
          <MenuItem disabled key="no-options">
            No options found
          </MenuItem>
        );
      }

      // Regular options
      opts.forEach((opt) => {
        const val = getOptionValue(opt);
        const label = getOptionLabel(opt);
        const checked = fieldValue.includes(val);

        menuItems.push(
          <MenuItem key={val} value={val}>
            {multiple && <Checkbox checked={checked} />}
            <ListItemText
              primary={label}
              primaryTypographyProps={{ style: { textTransform: 'none' } }}
            />
          </MenuItem>
        );
      });

      return menuItems;
    };

    return (
      <>
        <Select
          name={fieldName}
          multiple={multiple}
          value={fieldValue}
          onChange={handleChange}
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            ...elementStyle,
            minWidth: 180,
            '& .MuiSelect-select': { py: 1.5 },
          }}
          required={required}
          renderValue={renderValue}
          MenuProps={{
            PaperProps: { style: { maxHeight: 300 } },
          }}>
          {renderOptions(filterOptions)}
        </Select>

        {useFormik && fieldProps.meta.touched && fieldProps.meta.error && (
          <FormHelperText error id={`${fieldName}-error`}>
            {fieldProps.meta.error}
          </FormHelperText>
        )}
      </>
    );
  };

  if (useFormik) {
    return <Field name={fieldName}>{renderSelect}</Field>;
  }

  return renderSelect({ field: { value }, form: {}, meta: {} });
};

CommonDropdown.propTypes = {
  fieldName: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func,
  options: PropTypes.array.isRequired,
  elementStyle: PropTypes.object,
  required: PropTypes.bool,
  multiple: PropTypes.bool,
  useFormik: PropTypes.bool,
  displayMode: PropTypes.oneOf(['chips', 'comma']),
  showSelectAll: PropTypes.bool,
  enableSearch: PropTypes.bool,
};

CommonDropdown.defaultProps = {
  elementStyle: {},
  required: false,
  multiple: false,
  useFormik: false,
  displayMode: 'comma',
  showSelectAll: false,
  enableSearch: false,
};

export default CommonDropdown;
